<script lang="ts">
    import Spinner from '../Spinner.svelte';
    import type { PerformanceDialogDefinition } from 'header/types';

    interface Props {
        definition: PerformanceDialogDefinition;
    }

    let { definition }: Props = $props();

    let performanceCategorySummary = $derived(
        definition.performanceStore.performanceCategorySummary,
    );
    let isLoading = $derived(definition.performanceStore.totalDataCallTime == 0);
    let totalTime = $derived(performanceCategorySummary.reduce((acc, cur) => acc + cur.time, 0));
    let error = $derived(
        definition.performanceStore.totalDataCallTime == 0 &&
            !definition.performanceStore.transitions.some(({ from }) =>
                definition.performanceStore.actionsTransitions.RENDER_VIEW.includes(from),
            ),
    );

    const maxWidth = 200;

    function getBarWidth(performanceTime: number) {
        return (performanceTime / totalTime) * maxWidth;
    }
    function getOffset(index: number) {
        if (index === 0) {
            return 0;
        } else {
            const previousWidths = performanceCategorySummary
                .slice(0, index)
                .map(item => getBarWidth(item.time));
            const cumulativeWidth = previousWidths.reduce((acc, width) => acc + width, 0);
            return cumulativeWidth;
        }
    }
    function displayTime(time: number) {
        const seconds = time / 1000;
        return seconds < 0.01 ? '< 0.01' : seconds.toFixed(2);
    }
    function isAboveLimit(index?: number) {
        return index !== undefined
            ? performanceCategorySummary[index].time > performanceCategorySummary[index].threshold
            : performanceCategorySummary.some(({ time, threshold }) => time > threshold);
    }
</script>

<div class="performance">
    {#if isLoading}
        <div class="spinner">
            <Spinner />
        </div>
    {:else if error}
        <div class="alert">
            Requested resource is unavailable or there was some issue with related data
        </div>
    {:else}
        {#key performanceCategorySummary}
            <div class="graph-table">
                <div class="graph-header">
                    <div class="header-title">Total time:</div>
                    <div class="header-time" class:warning={isAboveLimit()}>
                        {displayTime(totalTime)} s
                    </div>

                    <div class="header-progress" style="width: {maxWidth}px;" />
                </div>
                {#each performanceCategorySummary as category, index}
                    <div class="graph-row">
                        <div style="position: relative;">
                            {#if isAboveLimit(index)}
                                <i class="material-icons icon">error</i>
                            {/if}

                            <span class="row-title">{category.title}:</span>
                        </div>
                        <div class="row-time">{displayTime(category.time)} s</div>
                        <div
                            class="row-progress"
                            style="margin-left:{getOffset(index)}px; width: {getBarWidth(
                                category.time,
                            )}px;"
                        />
                    </div>
                {/each}
            </div>
            {#each performanceCategorySummary as category, index}
                {#if isAboveLimit(index)}
                    <div class="description">
                        {category.description}
                    </div>
                {/if}
            {/each}
        {/key}
    {/if}
</div>

<style lang="scss" scoped>
    .spinner {
        height: 30px;
    }
    .graph-table {
        margin-left: 20px;
    }
    .graph-header,
    .graph-row {
        display: grid;
        grid-template-columns: 200px 100px 200px;
        margin-bottom: 1rem;
    }
    .header-progress,
    .row-progress {
        min-width: 1px;
        height: 20px;
        background-color: #145376;
    }
    .warning {
        color: #f7cd44;
        font-weight: bold;
    }

    .header-title {
        font-weight: bold;
    }
    .row-title {
        margin-left: 20px;
    }
    .icon {
        position: absolute;
        color: #f4a103;
        font-size: 18px;
        left: -2px;
    }
    .description,
    .footer {
        margin-top: 20px;
        max-width: 580px;
        white-space: pre-line;
    }
    .alert {
        font-size: 22px;
        color: #f4a103;
        width: 280px;
        text-align: center;
    }
</style>
